"use client";

import { ChevronDownIcon, Search } from "lucide-react";
import { Button } from "../ui/button";
import { Skeleton } from "../ui/skeleton";
import type { ProjectsSortBy } from "@/lib/get-projects";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { INITIAL_PAGE_SIZE, PAGE_SIZE } from "@/lib/get-projects";
import { cn } from "@/lib/utils";
import { useProjectsList } from "@/hooks/use-projects-list";
import { imagineRoutingStore } from "@/wrapper/imagine-routing.store";
import { useRef } from "react";
import { ProjectOptionsDropdown } from "./project-options-dropdown";
import { ProjectCard, projectItemCn } from "./project-card";

const sortByOptions = [
  {
    label: "Last edited",
    value: "updatedAt",
  },
  {
    label: "Created",
    value: "createdAt",
  },
];

export function MyProjectsSection() {
  const navigate = imagineRoutingStore.use.navigate();
  const containerRef = useRef<HTMLDivElement>(null);
  const {
    isInitialLoading,
    isPageLoading,
    projects,
    totalProjects,
    search,
    setSearch,
    sortBy,
    setSortBy,
    loadMoreProjects,
  } = useProjectsList("my-projects", containerRef);

  return (
    <div
      className="mx-auto max-w-6xl py-12 px-4 w-full min-w-0"
      ref={containerRef}
    >
      <h2 className="mb-4 text-2xl font-normal text-foreground leading-tight">
        My projects
      </h2>

      <div className="mb-6">
        {/* Search and filter */}
        <div className="flex items-center justify-between gap-4 w-full">
          <div className="relative flex-1 min-w-0">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              type="text"
              placeholder="Search by name"
              className="w-full rounded-md pl-9 max-w-120"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>

          <Select
            value={sortBy}
            onValueChange={(value) => setSortBy(value as ProjectsSortBy)}
          >
            <SelectTrigger className="w-52 flex-shrink-0 flex justify-between items-center">
              <div className="flex items-center gap-2">
                <div className="text-sm text-muted-foreground">Sort by</div>
              </div>
              <div className="flex items-center gap-2">
                <SelectValue placeholder="Sort by" />
                <ChevronDownIcon className="size-4 opacity-50" />
              </div>
            </SelectTrigger>
            <SelectContent>
              {sortByOptions.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Projects grid */}
      <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3 w-full">
        {isInitialLoading ? (
          <ProjectsSkeleton count={INITIAL_PAGE_SIZE} />
        ) : (
          projects?.map((project) => (
            <div
              key={project.id}
              className="min-w-0 w-full flex justify-center sm:justify-start"
            >
              <ProjectCard
                project={project}
                onClick={() =>
                  navigate(
                    {
                      id: "project",
                      props: { projectId: project.id, region: "fra" },
                    },
                    {
                      animate: {
                        promptSection: {
                          delay: 400,
                        },
                        projectsSection: {
                          delay: 0,
                        },
                        navigationDelay: 200,
                      },
                    },
                  )
                }
                actions={<ProjectOptionsDropdown project={project} />}
              />
            </div>
          ))
        )}

        {isPageLoading && <ProjectsSkeleton count={PAGE_SIZE} />}
      </div>
      {!projects?.length && !isInitialLoading && (
        <div className="w-full my-4 rounded-2xl shadow-[-76px_209px_62px_0_rgba(0,0,0,0.00),-49px_134px_57px_0_rgba(0,0,0,0.00),-12px_33px_36px_0_rgba(0,0,0,0.01),-3px_8px_20px_0_rgba(0,0,0,0.01)] dark:shadow-[0_0_0_1px_rgba(0,0,0,0.25)] bg-gradient-to-b from-[#F5F5F7] to-[#EAEAEC] dark:from-[#36363C] dark:to-[#2C2C30] p-[1px]">
          <div className="bg-background rounded-2xl flex flex-col items-center gap-1.5 p-8">
            <p className="text-xl text-foreground font-normal">
              Create your first project
            </p>
            <p className="text-sm text-muted-foreground font-light">
              No projects yet. Create one to get started.
            </p>
          </div>
        </div>
      )}
      {projects?.length > 0 &&
        projects.length < totalProjects &&
        !isPageLoading && (
          <div className="flex justify-center w-full mt-6">
            <Button
              variant="outline"
              onClick={() => loadMoreProjects()}
              disabled={isPageLoading}
              loading={isPageLoading}
            >
              Show more
            </Button>
          </div>
        )}
    </div>
  );
}

function AnimatedProjectWrapper({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return <div className={cn(projectItemCn, className)}>{children}</div>;
}

function ProjectsSkeleton({ count }: { count: number }) {
  return Array.from({ length: count }).map((_, i) => (
    <AnimatedProjectWrapper
      key={i}
      className={cn(
        "h-64 flex flex-col rounded-2xl p-[5px] animate-pulse gap-y-2",
        "bg-muted/30 dark:bg-muted/10",
      )}
    >
      <Skeleton key={i} className="flex-1 w-full" />
      <div className="rounded-md py-2 px-2">
        <Skeleton className="h-5 w-3/5" />
      </div>
    </AnimatedProjectWrapper>
  ));
}
